import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import de from './locales/de.json';
import en from './locales/en.json';
import uk from './locales/uk.json';

export const LANGUAGES = ['de', 'en', 'uk'] as const;
export type Language = (typeof LANGUAGES)[number];
const STORAGE_KEY = 'wg.language';

export const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);

/** The supported language for an i18next language tag ('uk-UA' → 'uk'); German otherwise. */
export function toLanguage(tag: string | undefined): Language {
  const base = (tag ?? '').toLowerCase().split('-')[0];
  return isLanguage(base) ? base : 'de';
}

/** Intl locale for dates, numbers and prices. */
const INTL_LOCALES: Record<Language, string> = { de: 'de-DE', en: 'en-GB', uk: 'uk-UA' };
export const intlLocale = (language: string) => INTL_LOCALES[toLanguage(language)];

function initialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // storage blocked: fall back to the browser language
  }
  const browser = typeof navigator !== 'undefined' ? navigator.language.toLowerCase() : '';
  if (browser.startsWith('en')) return 'en';
  if (browser.startsWith('uk')) return 'uk';
  return 'de';
}

void i18n.use(initReactI18next).init({
  resources: { de: { translation: de }, en: { translation: en }, uk: { translation: uk } },
  lng: initialLanguage(),
  supportedLngs: LANGUAGES,
  nonExplicitSupportedLngs: true,
  fallbackLng: 'de',
  interpolation: {
    escapeValue: false, // React escapes
    // The public brand is configurable (BRAND_NAME); texts say {{brand}}, never the name itself.
    defaultVariables: { brand: __BRAND_NAME__, verifyPrefix: __VERIFY_PREFIX__ },
  },
  returnNull: false,
});

export function setLanguage(language: Language) {
  void i18n.changeLanguage(language);
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // not persisted: fine
  }
}

// <html lang> follows the UI language, however it changes (switcher, profile, tests).
const syncHtmlLang = (language: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = toLanguage(language);
};
i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang(i18n.language);

/** 24.09.2026, 14:05 in the account's time zone (PRD §6). */
export function formatDateTime(iso: string, language: string, timeZone?: string) {
  return new Intl.DateTimeFormat(intlLocale(language), {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

const DAY_MS = 86_400_000;
const MINUTE_MS = 60_000;

const formats = new Map<string, Intl.DateTimeFormat>();
/**
 * One Intl.DateTimeFormat per locale and options: making one costs far more than using it, and
 * «Erreichbarkeit» writes up to 200 periods (review SP-A2.165).
 */
function dateFormat(locale: string, options: Intl.DateTimeFormatOptions) {
  const key = `${locale}|${JSON.stringify(options)}`;
  let format = formats.get(key);
  if (!format) formats.set(key, (format = new Intl.DateTimeFormat(locale, options)));
  return format;
}

/** Minutes the zone's clock is ahead of UTC at that moment (120 for MESZ). */
function utcOffset(date: Date, timeZone?: string) {
  const parts = dateFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
    ...(timeZone ? { timeZone } : {}),
  }).formatToParts(date);
  const part = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const clock = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
  );
  // The clock has whole minutes: so has the moment it is compared with (seconds of an incident).
  return (clock - Math.floor(date.getTime() / MINUTE_MS) * MINUTE_MS) / MINUTE_MS;
}

/** Whether the clock shows that time twice in the zone: the hour after the clocks go back. */
function shownTwice(date: Date, timeZone?: string) {
  const offset = utcOffset(date, timeZone);
  return [-DAY_MS, DAY_MS].some((step) => {
    const other = utcOffset(new Date(date.getTime() + step), timeZone);
    const twin = new Date(date.getTime() + (offset - other) * MINUTE_MS);
    return other !== offset && utcOffset(twin, timeZone) === other;
  });
}

/**
 * A period in the account's time zone, short in every language (SP-UX12f): 12.10.2026, 10:11–10:16
 * within one day, both dates over midnight, an open end as "…". The night the clocks change it
 * names the zone (SP-A2.165, as the status page since SP-A2.81): at each end when the period
 * crosses the change («02:30 MESZ – 02:10 MEZ», not «02:30–02:10»), once for a time shown twice.
 */
export function formatDateTimeRange(
  from: string,
  to: string | null,
  language: string,
  timeZone?: string,
) {
  const options: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  };
  const locale = intlLocale(language);
  const start = new Date(from);
  const end = to ? new Date(to) : null;
  const zoned = dateFormat(locale, { ...options, timeZoneName: 'short' });
  if (end && utcOffset(start, timeZone) !== utcOffset(end, timeZone)) {
    const endFormat = sameDay(start, end, timeZone)
      ? dateFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
          ...(timeZone ? { timeZone } : {}),
        })
      : zoned;
    return `${zoned.format(start)} – ${endFormat.format(end)}`;
  }
  const twice = shownTwice(start, timeZone) || (end !== null && shownTwice(end, timeZone));
  const format = twice ? zoned : dateFormat(locale, options);
  if (!end) return `${format.format(start)} – …`;
  return format.formatRange(start, end);
}

export function formatDate(iso: string, language: string, timeZone?: string) {
  return new Intl.DateTimeFormat(intlLocale(language), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/**
 * The day something ends – free access, a trial, a period paid by invoice (SP-A2.188). A day
 * chosen in the admin is stored as the end of that day in UTC, 23:59:59 UTC, and the API counts
 * it so: such an end is that day wherever the reader is (in Berlin it is already 01:59 of the
 * next one). Any other end is a moment – an access ended, the trial of a sign-up at the end of its
 * 14th day in the account's zone (SP-A2.223), a period from noon UTC – and falls on its day in the
 * reader's zone.
 */
export function formatEndDay(iso: string, language: string, timeZone?: string) {
  return formatDate(iso, language, endDayZone(new Date(iso), timeZone));
}

/** The zone an end's day is named in: UTC for a day picked in the admin (formatEndDay). */
function endDayZone(end: Date, timeZone?: string) {
  const picked =
    end.getUTCHours() === 23 && end.getUTCMinutes() === 59 && end.getUTCSeconds() === 59;
  return picked ? 'UTC' : timeZone;
}

/**
 * Days from the reader's today to the day something ends, as formatEndDay names it (SP-A2.223):
 * "noch 14 Tage (bis 30.10.)" on the day of a sign-up whose trial ends with 30.10, 0 on that last
 * day – also on a day picked in the admin that still runs by UTC past midnight in Berlin. Without
 * a zone, the browser's of this moment: a cached format would keep the zone it was made in.
 */
export function daysToEndDay(iso: string, now: Date, timeZone?: string) {
  const end = new Date(iso);
  const reader = timeZone ?? new Intl.DateTimeFormat().resolvedOptions().timeZone;
  const day = (at: Date, zone: string) =>
    Date.parse(
      dateFormat('en-CA', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: zone,
      }).format(at),
    );
  const days = (day(end, endDayZone(end, reader) ?? reader) - day(now, reader)) / 86_400_000;
  return Math.max(0, Math.round(days));
}

/** 14:05 in the account's time zone. */
export function formatTime(iso: string, language: string, timeZone?: string) {
  return new Intl.DateTimeFormat(intlLocale(language), {
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

/** Whether two moments fall on the same calendar day in the time zone. */
export function sameDay(a: Date, b: Date, timeZone?: string) {
  const day = dateFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  });
  return day.format(a) === day.format(b);
}

/** "vor 48 Sek.", "vor 3 Tagen": how long ago, in the largest whole unit (overview.html). */
export function formatAgo(iso: string, language: string, now = Date.now()) {
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  const ago = new Intl.RelativeTimeFormat(intlLocale(language), {
    numeric: 'always',
    style: 'short',
  });
  if (seconds < 60) return ago.format(-seconds, 'second');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return ago.format(-minutes, 'minute');
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return ago.format(-hours, 'hour');
  return ago.format(-Math.floor(hours / 24), 'day');
}

export function formatNumber(value: number, language: string, digits = 1) {
  return new Intl.NumberFormat(intlLocale(language), {
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatDuration(seconds: number, language: string) {
  const minutes = Math.max(1, Math.round(seconds / 60));
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const lang = toLanguage(language);
  if (lang === 'en') return h > 0 ? `${h} h ${m} min` : `${m} min`;
  if (lang === 'uk') return h > 0 ? `${h} год ${m} хв` : `${m} хв`;
  return h > 0 ? `${h} Std. ${m} Min.` : `${m} Min.`;
}

export default i18n;
